import React, { useEffect, useRef, useState } from 'react';
import { ReactEditor, useSelected, useFocused, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import HelptapEditor from '../../helpers';
import BaseElement from '../base';

import ShowMore from './show-more';
import { hasMoreThanPreview, buildMoreRowsHTML, fetchMoreRows } from './more-rows';

import './index.scss';

function buildEmptyMoreRows(element) {

  return {
    html: '',
    count: 0,
    hasMore: hasMoreThanPreview(element)
  };

}

function DataCollection(props) {

  const element = props.element;
  const elementId = element.id;
  const editor = useSlateStatic();
  const selected = useSelected();
  const focused = useFocused();
  const elementPath = ReactEditor.findPath(editor, element);
  const moreRowsId = `more-rows-${element.properties.id}`;

  const [moreRows, setMoreRows] = useState(buildEmptyMoreRows(element));
  const [loadingMoreRows, setLoadingMoreRows] = useState(false);
  const [moreRowsFailed, setMoreRowsFailed] = useState(false);
  const moreRowsRequest = useRef(0);

  useEffect(function resetMoreRows() {

    moreRowsRequest.current = moreRowsRequest.current + 1;

    setMoreRows(buildEmptyMoreRows(element));
    setLoadingMoreRows(false);
    setMoreRowsFailed(false);

  }, [element]);

  useEffect(function onLoad() {

    tryInitialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initializ() {

    const filtersManager = application.dataManagers.get('DataCollectionFiltersManager');
    const pageId = props.parentEntityId;
    const result = await serverInterface.startup.dataCollections.embedCollectionInPage(element.properties.collectionId, pageId, element.properties.rowsCount);
    console.log(JSON.stringify(result, null, 2), 'result');
    await filtersManager.loadAllForce(result.collection._id);

    HelptapEditor.dataCollection.initialize(editor, element, elementPath, result);

    filtersManager.pushFilterReference(result.collection._id, result.filterId, {
      type: 'PAGE',
      _id: `${pageId}:${element.properties.id}`
    });

  }

  async function tryInitialize() {

    if (element.properties.initialized === true) {
      await application.dataManagers.get('DataCollectionFiltersManager').loadAll(element.properties.collectionId);
      return;
    }

    initializ();

  }

  async function loadMoreRows() {

    const request = moreRowsRequest.current;
    const shownCount = element.children.length + moreRows.count;

    setLoadingMoreRows(true);

    let result;

    try {
      result = await fetchMoreRows(props.parentEntityId, element, shownCount);
    } catch (error) {

      if (request === moreRowsRequest.current) {
        setLoadingMoreRows(false);
        setMoreRowsFailed(true);
      }

      return;
    }

    if (request !== moreRowsRequest.current) {
      return;
    }

    setMoreRows({
      html: moreRows.html + result.html,
      count: moreRows.count + result.count,
      hasMore: result.hasMore
    });
    setLoadingMoreRows(false);
    setMoreRowsFailed(false);

  }

  function renderMoreRows() {

    if (moreRows.count === 0) {
      return null;
    }

    const moreRowsAttributes = {
      id: moreRowsId,
      className: 'data-collection-more-rows',
      dangerouslySetInnerHTML: {
        __html: buildMoreRowsHTML(element, moreRowsId, moreRows.html)
      }
    };

    return <div {...moreRowsAttributes} />;

  }

  function renderShowMore() {

    if (moreRows.hasMore === false) {
      return null;
    }

    const showMoreProperties = {
      loading: loadingMoreRows,
      failed: moreRowsFailed,
      onClick: loadMoreRows
    };

    return <ShowMore {...showMoreProperties} />;

  }

  function renderDataCollection() {

    const { attributes, children } = props;

    if (element.properties.initialized === false) {
      const columnContainerAttributes = {
        contentEditable: false
      };
      return <div {...columnContainerAttributes}>
        <ht-spinner />
        {children}
      </div>;
    }

    const columnContainerAttributes = {
      ...attributes,
      className: 'data-collection-wrapper'
    };
    const columnAttributes = {
      id: elementId,
      className: `data-collection-layout ${selected && focused ? 'selected' : ''}`,
      contentEditable: false,
      style: {
        gridTemplateColumns: `repeat(${element.properties.columnsCount}, minmax(0, 1fr))`
      }
    };

    return (
      <div {...columnContainerAttributes}>
        <div {...columnAttributes}>
          {children}
          {renderMoreRows()}
        </div>
        {renderShowMore()}
      </div>
    );
  }

  function renderMain() {
    const baseElementProperties = {
      ...props,
      customNodeControls: []
    };

    return (
      <BaseElement {...baseElementProperties}>
        {renderDataCollection()}
      </BaseElement>
    );
  }

  return renderMain();
}

export * from './row';

export default DataCollection;